'use client';

import { useState } from 'react';

import { cn } from '@/lib/utils';

import { SvgIcon } from '@/components/SvgIcon';

import Button from '@/components/atoms/Button';
import { Carousel, CarouselHandle } from '@/components/atoms/Carousel';
import { IconButton } from '@/components/atoms/IconButton';
import Section from '@/components/atoms/Section/Section';
import { carouselConfig } from '@/features/HomepageHero/constants/carousel';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import {
  MembersCarouselItem,
  MembersCarouselItemProps,
} from './MembersCarouselItem';

import iconArrowLeftSrc from '~/icons/arrow-left.svg';
import iconArrowRightSrc from '~/icons/arrow-right.svg';

export type MembersCarouselProps = {
  slides: MembersCarouselItemProps[];
};

export function MembersCarousel({ slides }: MembersCarouselProps) {
  const [carousel, setCarousel] = useState<CarouselHandle>();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  return (
    <Section
      data-tracker-section={SECTION_NAMES.MEMBERS_CAROUSEL_BLOCK}
      className='w-full h-full xl:grid-cols-[48px_1fr_48px] xl:grid-rows-1 items-center justify-between xl:grid'
    >
      <div className='shrink-0 hidden xl:block'>
        <IconButton
          onClick={() => carousel?.slidePrev()}
          color='secondary'
          size='l'
          classNames={{
            root: 'dark:bg-gray-100 bg-gray-100 text-base-black rounded-full dark:hover:bg-gray-300  hover:bg-gray-200',
          }}
        >
          <SvgIcon src={iconArrowLeftSrc} className='h-8 w-8' />
        </IconButton>
      </div>
      <div
        className={cn(
          'transform-3d xl:max-w-[1094px] justify-start relative z-[1] lg:mx-auto'
        )}
      >
        <Carousel
          config={{
            onSwiper: (handle) => setCarousel(handle),
            onSlideChange: (handle) => setActiveSlideIndex(handle.realIndex),
            ...carouselConfig,
          }}
          sideGradient={false}
          classNames={{
            root: 'lg:max-w-[1094px]',
          }}
          items={slides}
          renderItem={({ item, index }) => {
            return (
              <MembersCarouselItem
                isActive={index === activeSlideIndex}
                name={item.name}
                jobTitle={item.jobTitle}
                description={item.description}
                image={item.image}
              />
            );
          }}
        />
        <div>
          <div className='flex items-center md:mt-16 justify-center'>
            <div className='flex gap-x-3'>
              {slides.map((_, i) => (
                <Button
                  key={_.name}
                  noStyles
                  classNames={{
                    root: cn(
                      'h-3 w-3 rounded-full duration-150',
                      'bg-gray-500',
                      i !== activeSlideIndex && 'bg-gray-300 hover:bg-gray-400'
                    ),
                  }}
                  onClick={() =>
                    typeof carousel?.slideToLoop === 'function'
                      ? carousel.slideToLoop(i)
                      : carousel?.slideTo(i)
                  }
                  aria-label={`${i + 1}`}
                >
                  <div className='sr-only'>{i + 1}</div>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className='shrink-0 hidden xl:block'>
        <IconButton
          onClick={() => carousel?.slideNext()}
          color='secondary'
          size='l'
          classNames={{
            root: 'dark:bg-gray-100 bg-gray-100 text-base-black rounded-full dark:hover:bg-gray-300  hover:bg-gray-200',
          }}
        >
          <SvgIcon src={iconArrowRightSrc} className='h-8 w-8' />
        </IconButton>
      </div>
    </Section>
  );
}
